import { createI18n } from 'vue-i18n'
import en from '@/locales/en.json'

/**
 * A instância do i18n mora aqui, e não no main.ts, porque módulos que não são
 * componentes também precisam traduzir e não têm o $tt: os operadores, os tipos,
 * as classes-base de rota e o StateColorizer. Este arquivo só importa o vue-i18n e
 * os JSONs, então ninguém cria ciclo ao consumi-lo.
 *
 * ATENÇÃO: chamar t() no momento do uso, nunca no carregamento do módulo —
 * traduzir em tempo de import congelaria o idioma no que estivesse ativo então.
 *
 * `legacy: true` de propósito: é o modo que mantém o `$t` e o `this.$i18n` dos
 * componentes de opções, que é como as 80 telas traduzem. Sem ele seria preciso trocar
 * cada uma para o useI18n() da Composition API, o que não é migração de framework, é
 * reescrita.
 */

const localesContext = require.context('./locales', true, /\.json$/)

const messages = Object.fromEntries(
    localesContext.keys().map((fileName) => {
      const locale = fileName.replace('./', '').replace('.json', '')
      return [locale, localesContext(fileName)]
    })
)

/**
 * O idioma que o app TEM para o que se pediu — o preferido da conta, ou o do navegador: o exato, ou
 * a mesma língua em outra variante (pt-PT → pt-BR, es-MX → es — decisão do usuário: antes o pt-PT
 * caía no inglês), ou o inglês, a reserva.
 *
 * Aqui, e não junto do `applyLanguage`, porque o próprio i18n precisa dela para nascer — e este
 * arquivo não pode importar o Vuetify, que importa este.
 */
export function resolveLanguage(asked: string | null | undefined): string {
  const available = Object.keys(messages)
  const wanted = (asked || '').toLowerCase()
  const base = wanted.split('-')[0]
  return available.find(language => language.toLowerCase() === wanted)
      || available.find(language => language.split('-')[0].toLowerCase() === base)
      || 'en'
}

const i18n = createI18n({
  legacy: true,
  locale: resolveLanguage(navigator.language),
  fallbackLocale: 'en',
  silentFallbackWarn: true,
  messages
})

/** Chave válida = chave existente no en.json, que é a fonte de verdade do tipo. */
export type MessageKey = keyof typeof en

/** Os valores de interpolação de uma mensagem — nomeados ou posicionais. */
export type TranslationValues = Record<string, unknown> | unknown[]

/** Tradução para uso fora de componentes. */
export function t(key: MessageKey, values?: TranslationValues): string {
  return i18n.global.t(key as string, values as never)
}

/**
 * Tradução com plural: a forma certa da mensagem (`singular | plural`) sai de `count`.
 *
 * Sem `count`, vale 1 — o mesmo padrão do `tc` do vue-i18n 8, e é o que faz uma contagem
 * que não veio do servidor sair no singular, como saía.
 *
 * O vue-i18n 11 tirou o `tc`/`$tc` e passou o plural para o próprio `t`, com a contagem
 * como terceiro argumento. Este é o substituto, para as telas continuarem escrevendo
 * `$tc(chave, n, valores)` como antes.
 */
export function tc(key: MessageKey, count?: number, values: Record<string, unknown> = {}): string {
  return i18n.global.t(key as string, values as never, count ?? 1)
}

/** Idioma efetivo, normalizado para o prefixo de duas letras (pt-BR -> pt). */
export function shortLocale(): string {
  const locale = i18n.global.locale as unknown as { value?: string } | string
  const current = typeof locale === 'string' ? locale : locale.value
  return (current || 'en').split('-')[0].toLowerCase()
}

export default i18n
